import { useEffect, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WealthShell } from "@/pages/viveka/wealth/WealthShell";
import { BUCKET_TONE, cr, pct, useWealthPosition } from "@/lib/wealth";
import { EmptyState } from "@/components/shared";
import { toast } from "sonner";

const DesiredPolicy = ({ position, reload }) => {
  const { tenantId } = useAuth();
  const [form, setForm] = useState({ safety: "", market: "", aspirational: "" });

  useEffect(() => {
    const p = position.policy;
    setForm({
      safety: p?.desired_safety_pct ?? "",
      market: p?.desired_market_pct ?? "",
      aspirational: p?.desired_aspirational_pct ?? "",
    });
  }, [position.policy]);

  const total = ["safety", "market", "aspirational"]
    .reduce((s, k) => s + (Number(form[k]) || 0), 0);

  const save = async () => {
    try {
      await api.put(`/tenants/${tenantId}/wealth/policy`, {
        desired_safety_pct: Number(form.safety) || 0,
        desired_market_pct: Number(form.market) || 0,
        desired_aspirational_pct: Number(form.aspirational) || 0,
      });
      toast.success("Desired allocation saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="panel p-6" data-testid="desired-policy">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="label-caps">Family Board policy</p>
          <h3 className="font-display text-lg text-navy">Desired allocation</h3>
        </div>
        <p className={`font-mono text-sm font-semibold tabular-nums ${
          total === 100 ? "text-success" : "text-destructive"}`} data-testid="desired-total">
          Total {total}%
        </p>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[["safety", "Safety"], ["market", "Market"], ["aspirational", "Aspirational"]].map(([k, label]) => (
          <label key={k} className="block">
            <span className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${BUCKET_TONE[k].chip}`}>
              {label}
            </span>
            <input type="number" step="any" min="0" max="100" value={form[k]}
                   disabled={!position.can_edit} data-testid={`desired-${k}`}
                   onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                   className="mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-right font-mono text-sm text-navy outline-none focus:border-plum disabled:opacity-60" />
          </label>
        ))}
      </div>
      {position.can_edit && (
        <button onClick={save} data-testid="desired-save"
                className="mt-4 rounded-full bg-plum px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
          Save desired allocation
        </button>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">
        Must total 100%. The gap column below and the business-to-family bridge both read these targets.
      </p>
    </div>
  );
};

export default function WealthAllocator() {
  const { position, reload } = useWealthPosition();
  const [open, setOpen] = useState("safety");

  const buckets = position?.buckets || [];
  const active = buckets.find((b) => b.bucket === open);
  const pie = buckets.filter((b) => b.value > 0)
    .map((b) => ({ name: b.label, value: b.value, bucket: b.bucket }));

  return (
    <WealthShell
      title="Wealth allocator"
      description="Risk Allocation is the split across Safety, Market and Aspirational. Click a bucket to see Asset Allocation within it, by asset class."
    >
      {!buckets.length ? (
        <EmptyState testid="allocator-empty" title="Nothing to allocate yet"
                    message="Add assets in the register and the buckets will calculate themselves." />
      ) : (
        <div className="space-y-6" data-testid="wealth-allocator">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.3fr]">
            <div className="panel p-6">
              <p className="label-caps">Risk allocation</p>
              <div className="mt-2 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pie} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="82%"
                         paddingAngle={2}>
                      {pie.map((p) => <Cell key={p.bucket} fill={BUCKET_TONE[p.bucket].color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => cr(v)}
                             contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <p className="text-center font-mono text-lg font-semibold tabular-nums text-navy"
                 data-testid="allocator-net-worth">
                {cr(position.totals.net_worth)}
              </p>
              <p className="text-center text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                Total family net worth
              </p>
            </div>

            <div className="panel overflow-hidden">
              <div className="grid grid-cols-[1.5fr_1fr_0.7fr_0.7fr_0.8fr] gap-2 border-b border-border bg-muted/40 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                <span>Bucket</span><span className="text-right">Current value</span>
                <span className="text-right">Current %</span><span className="text-right">Desired %</span>
                <span className="text-right">Gap</span>
              </div>
              <div className="divide-y divide-border">
                {buckets.map((b) => (
                  <button key={b.bucket} onClick={() => setOpen(b.bucket)}
                          data-testid={`allocator-bucket-${b.bucket}`}
                          className={`grid w-full grid-cols-[1.5fr_1fr_0.7fr_0.7fr_0.8fr] items-center gap-2 px-5 py-4 text-left transition-colors hover:bg-muted/40 ${
                            open === b.bucket ? "bg-plum/5" : ""}`}>
                    <div>
                      <p className="text-sm font-semibold text-navy">{b.label}</p>
                      <p className="text-[11px] text-muted-foreground">{b.intent}</p>
                    </div>
                    <p className="text-right font-mono text-sm tabular-nums text-navy">{cr(b.value)}</p>
                    <p className="text-right font-mono text-sm font-semibold tabular-nums text-navy">{pct(b.current_pct)}</p>
                    <p className="text-right font-mono text-sm tabular-nums text-foreground/70">{pct(b.desired_pct)}</p>
                    <p className={`text-right font-mono text-sm font-semibold tabular-nums ${
                      b.gap_pct == null ? "text-muted-foreground"
                        : b.gap_pct > 0 ? "text-[hsl(28_82%_38%)]"
                        : b.gap_pct < 0 ? "text-destructive" : "text-success"}`}
                       data-testid={`allocator-gap-${b.bucket}`}>
                      {b.gap_pct == null ? "—" : `${b.gap_pct > 0 ? "+" : ""}${b.gap_pct.toFixed(2)}%`}
                    </p>
                  </button>
                ))}
              </div>
              <p className="px-5 py-3 text-[11px] text-muted-foreground">
                Gap = current % − desired %. A positive gap is an over-weight versus the Board's target;
                negative is under-weight. Current % is calculated, never keyed.
              </p>
            </div>
          </div>

          <DesiredPolicy position={position} reload={reload} />

          {active && (
            <div className="panel overflow-hidden" data-testid={`allocator-detail-${active.bucket}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
                <div>
                  <p className="label-caps">Asset allocation within bucket</p>
                  <h3 className="font-display text-xl text-navy">{active.label}</h3>
                </div>
                <p className="font-mono text-sm tabular-nums text-navy">{cr(active.value)}</p>
              </div>
              <div className="divide-y divide-border">
                {active.classes.map((c) => (
                  <div key={c.asset_class} className="px-6 py-4" data-testid={`allocator-class-${c.asset_class.replace(/\W+/g, "-")}`}>
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm font-medium text-navy">{c.asset_class}</p>
                      <div className="flex items-center gap-5">
                        <p className="font-mono text-sm tabular-nums text-foreground/80">{cr(c.value)}</p>
                        <p className="w-20 text-right font-mono text-sm font-semibold tabular-nums text-navy">
                          {pct(c.pct_within_bucket)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className={`h-full rounded-full ${BUCKET_TONE[active.bucket].bar}`}
                           style={{ width: `${c.pct_within_bucket || 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border bg-muted/20 px-6 py-4">
                <p className="label-caps">Underlying assets</p>
                <div className="mt-3 space-y-2">
                  {active.assets.map((a) => (
                    <div key={a.id} className="flex flex-wrap items-center gap-3 text-sm"
                         data-testid={`allocator-asset-${a.id}`}>
                      <span className="font-medium text-navy">{a.name}</span>
                      <span className="label-caps">{a.asset_class}</span>
                      {a.test_value && (
                        <span className="rounded-full bg-warning/20 px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] text-[hsl(28_82%_32%)]">
                          test value
                        </span>
                      )}
                      <span className="ml-auto font-mono tabular-nums text-foreground/80">{cr(a.net_value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </WealthShell>
  );
}
